import { useId } from 'react'

import { parseISO } from 'date-fns'

import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { DatePickerWithControls, Label, SearchSelect } from '@fortem-monorepo/react-ui'
import type { AccruedInterests } from '@fortem-monorepo/web-sdk'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'

import { getBalanceToday, isFutureBalanceDate } from '@/lib/accrued-interest-dates'
import { useFunds } from '@/react-query/queries/funds/funds'

type Props = {
  params: AccruedInterests.Params
  onChange: (params: AccruedInterests.Params) => void
  disabled: boolean
  showDate?: boolean
  limitToToday?: boolean
}

export const AccruedInterestFilters = ({
  params,
  onChange,
  disabled,
  showDate = true,
  limitToToday = false,
}: Props) => {
  const { data: funds, query } = useFunds()
  const fundId = useId()
  return (
    <>
      <div className="space-y-1">
        <Label htmlFor={fundId}>Fund</Label>
        <SearchSelect
          id={fundId}
          size="sm"
          placeholder="Select a fund"
          loading={query.isFetching}
          disabled={disabled}
          options={funds.map((fund) => ({ value: fund.fund_name, label: fund.fund_name }))}
          value={params.fund_name}
          setValue={(fund_name) => onChange({ ...params, fund_name })}
        />
      </div>
      {showDate && (
        <div className="space-y-1" role="group" aria-label="Value date">
          <Label>Date</Label>
          <DatePickerWithControls
            size="sm"
            selected={parseISO(params.value_date)}
            buttonDisabled={disabled}
            disabled={limitToToday ? (date) => isFutureBalanceDate(toISO8601(date)) : undefined}
            onSelect={(date) => {
              if (date) onChange({ ...params, value_date: toISO8601(date) })
            }}
            todayButton={{ enabled: true, date: parseISO(getBalanceToday()) }}
          />
        </div>
      )}
      {query.isError && (
        <p role="alert" className="text-sm text-destructive">
          Unable to load funds. {getErrorMessage(query.error)}
        </p>
      )}
    </>
  )
}
